import { inject, Injectable } from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { LoginResponse, OidcSecurityService } from 'angular-auth-oidc-client';
import { EMPTY, merge, Observable, Subject } from 'rxjs';
import { catchError, filter, map, switchMap, take, tap, toArray } from 'rxjs/operators';

import { SSO_PROVIDER } from 'app/shared/components/sso-provider-button/sso-provider-button.component';

import { environment } from 'env/environment';

const SSO_PROVIDER_CUSTOM_PARMS: Record<SSO_PROVIDER, any> = {
  Google: {
    prompt: 'select_account'
  }
};

const SSO_PROVIDER_CLIENT_ID: Map<SSO_PROVIDER, string> = new Map([
  ['Google', environment.googleClientId]
]);

export interface ISingleSignOnToken {
  SSOProvider: SSO_PROVIDER | null;
  Token: string;
};

@UntilDestroy()
@Injectable({
  providedIn: 'root'
})
export class SingleSignOnService {
  private readonly securityService = inject(OidcSecurityService);

  private token$ = new Subject<ISingleSignOnToken>();

  public getSignOnToken(): Observable<ISingleSignOnToken> {
    return this.token$.asObservable();
  }

  public authorize(provider: SSO_PROVIDER, username?: string): void {
    const authOptions = {
      customParams: SSO_PROVIDER_CUSTOM_PARMS[provider] ? SSO_PROVIDER_CUSTOM_PARMS[provider] : {}
    };
    if (username) {
      authOptions.customParams.login_hint = username;
    }

    this.lookupConfig(provider)
      .pipe(
        catchError((error: unknown) => {
          console.error(error);

          return EMPTY;
        })
      )
      .subscribe((configId) => this.securityService.authorize(configId, authOptions));
  }

  public checkAuth(): void {
    SSO_PROVIDER_CLIENT_ID.forEach((clientId, provider) => {
      this.lookupConfig(provider)
        .pipe(
          switchMap((configId) => this.securityService.checkAuth(undefined, configId)),
          catchError((error: unknown) => {
            console.error(error);

            return EMPTY;
          })
        )
        .subscribe((loginResponse: LoginResponse) => {
          const token = loginResponse.idToken;
          if (loginResponse.isAuthenticated) {
            this.token$.next({
              Token: token,
              SSOProvider: provider
            });
          }
          else {
            // signal they are not signed in
            this.token$.next({
              Token: '',
              SSOProvider: null
            });
          }
        });
    });
  }

  /**
   * Signs out of every provider the user is authenticated with.
   * Emits once all providers are done; errors are surfaced to the caller rather than swallowed.
   */
  public logoutAll(): Observable<void> {
    const logoffs = Array.from(SSO_PROVIDER_CLIENT_ID.keys()).map((provider) => this.logoutProvider(provider));

    return merge(...logoffs)
      .pipe(
        toArray(),
        map(() => undefined)
      );
  }

  private logoutProvider(provider: SSO_PROVIDER): Observable<void> {
    return this.lookupConfig(provider)
      .pipe(
        switchMap((configId) => this.securityService.isAuthenticated(configId)
          .pipe(
            take(1),
            filter((isAuthenticated) => isAuthenticated),
            map(() => configId)
          )),
        tap((configId) => {
          this.securityService.logoff(configId);
          this.token$.next({
            Token: '',
            SSOProvider: null
          });
        }),
        map(() => undefined)
      );
  }

  public checkForLogins(): void {
    SSO_PROVIDER_CLIENT_ID.forEach((clientId, provider) => {
      this.lookupConfig(provider)
        .pipe(
          switchMap((configId) => this.securityService.getIdToken(configId)),
          catchError((error: unknown) => {
            console.error(error);

            return EMPTY;
          })
        )
        .subscribe((token: string) => {
          if (token) {
            this.token$.next({
              Token: token,
              SSOProvider: provider
            });
          }
        });
    });
  }

  /**
   * Resolves the configId for a provider, emitting nothing when the provider has no usable config.
   * getConfiguration() forces the lazy config loader to run, so the config cache is never read while cold.
   */
  private lookupConfig(provider: SSO_PROVIDER): Observable<string> {
    const clientId = SSO_PROVIDER_CLIENT_ID.get(provider);
    if (!clientId) {
      return EMPTY;
    }

    return this.securityService.getConfiguration()
      .pipe(
        take(1),
        map(() => this.securityService.getConfigurations().find((config) => config.clientId === clientId)?.configId),
        filter((configId): configId is string => !!configId)
      );
  }
}
